import { Button, Input, Search } from '@chain-zen/ui'
import { useMemo, useState } from 'react'

import type { IELDApp, IELDAsset } from '../../../../../../../data/queries/eld-apps'
import { useELDAssetLocationPreview } from '../../hooks/use-eld-asset-location-preview'
import type { TActiveAssetType } from '../../hooks/use-eld-asset-selection'
import { AssetItem } from './asset-item'

interface IAssetListProps {
  activeAssetType: TActiveAssetType
  assets: IELDAsset[]
  currentValue?: string | null
  isLoadingUpdate: boolean
  manualInputValue: string
  onAssetSelect: (asset: IELDAsset) => void
  onManualInputChange: (value: string) => void
  onManualSubmit: () => void
  onRemoveClick?: () => void
  selectingAsset: string | null
  selectedELDApp: IELDApp | null
  selectedItem: IELDAsset | null
}

/**
 * Asset list component for selecting trailers or trucks.
 * Displays a list of available assets with search functionality and manual input option.
 * Note: Loading state should be handled by the parent component using AssetListSkeleton.
 */
export const AssetList = ({
  activeAssetType,
  assets,
  currentValue,
  isLoadingUpdate,
  manualInputValue,
  onAssetSelect,
  onManualInputChange,
  onManualSubmit,
  onRemoveClick,
  selectingAsset,
  selectedELDApp,
  selectedItem
}: IAssetListProps) => {
  const assetTypeForQuery: 'Trailer' | 'Truck' = activeAssetType === 'trailer' ? 'Trailer' : 'Truck'
  const { previewLocation, previewState, clearPreview } = useELDAssetLocationPreview({
    assetType: assetTypeForQuery,
    selectedELDApp
  })
  const [searchValue, setSearchValue] = useState('')

  /**
   * Filter out assets without valid assetNumber and apply search filter.
   * useMemo is justified here for performance as this can be a large list
   * that gets re-filtered on every keystroke.
   */
  const filteredAssets = useMemo(() => {
    const validAssets = assets.filter((asset) => asset && asset.assetNumber)
    const trimmed = searchValue.trim().toLowerCase()
    if (!trimmed) return validAssets
    return validAssets.filter((asset) => asset.assetNumber.toLowerCase().includes(trimmed))
  }, [assets, searchValue])

  // Unified input logic
  const trimmedInput = manualInputValue.trim()
  const hasCurrentValue = !!currentValue
  const inputMatchesCurrent = hasCurrentValue && trimmedInput === currentValue
  const inputIsEmpty = !trimmedInput
  const inputIsDifferent = hasCurrentValue && trimmedInput && trimmedInput !== currentValue

  // Button state: Remove (input matches current), Replace (input different from current), Add (no current value)
  const getButtonAction = (): string => {
    if (inputMatchesCurrent) return 'remove'
    if (inputIsDifferent) return 'replace'
    return 'add'
  }
  const buttonAction = getButtonAction()
  const ACTION_LABELS: Record<string, string> = {
    add: 'Add',
    remove: 'Remove',
    replace: 'Replace'
  }
  const buttonLabel = isLoadingUpdate ? 'Saving...' : (ACTION_LABELS[buttonAction] ?? 'Add')

  const handleButtonClick = () => {
    if (buttonAction === 'remove') {
      onRemoveClick?.()
    } else {
      onManualSubmit()
    }
  }

  const isButtonDisabled = isLoadingUpdate || (buttonAction !== 'remove' && inputIsEmpty)
  const hintMessage = hasCurrentValue ? `Edit or remove current ${activeAssetType}` : `Add a ${activeAssetType} number`

  return (
    <div className="flex flex-col gap-3">
      {/* Unified input with dynamic button */}
      <div className="p-3 bg-muted/50 border border-border rounded-lg">
        <div className="text-sm text-muted-foreground mb-2">{hintMessage}</div>
        <div className="flex gap-2">
          <Input
            type="text"
            placeholder={`Enter ${activeAssetType} number...`}
            value={manualInputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onManualInputChange(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && !isButtonDisabled) {
                handleButtonClick()
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            variant={buttonAction === 'remove' ? 'destructive' : 'default'}
          >
            {buttonLabel}
          </Button>
        </div>
      </div>

      {/* Search and asset list */}
      {assets.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 py-8">
          <div className="rounded-full bg-muted p-3">
            <span className="text-2xl">{activeAssetType === 'trailer' ? '🚛' : '🚚'}</span>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground">No {activeAssetType}s available</p>
            <p className="text-xs text-muted-foreground mt-1">
              Use the custom entry above to add a {activeAssetType} number.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by number..."
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              className="w-full pl-9"
            />
          </div>

          {/* Asset list */}
          {filteredAssets.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center">No matches found.</div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto pr-1">
              {filteredAssets.map((asset, index) => (
                <AssetItem
                  key={asset.assetID || `asset-${index}`}
                  asset={asset}
                  activeAssetType={activeAssetType}
                  currentValue={currentValue}
                  selectedItem={selectedItem}
                  selectingAsset={selectingAsset}
                  isLoadingUpdate={isLoadingUpdate}
                  hasSelectedELDApp={!!selectedELDApp}
                  previewState={previewState}
                  previewLocation={previewLocation}
                  clearPreview={clearPreview}
                  onSelect={onAssetSelect}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
